import { useState, useEffect, type FC } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { orderQueryKey } from '@/api/registration';
import { type PrepareOutstandingPaymentRequest, prepareOutstandingPayment, type PaymentIntent, type EwayAccessCode, type SecurePayAccessCode, type NabTransactOptions } from '@/api/registration/payment';
import { type ApiResponse } from '@/api/tenantClient';
import { SkeletonText } from '@chakra-ui/react';
import BaseMessageBarError from '@/base/MessageBar/Error';
import { useTenantApi } from '@/account/hook/useTenantApi';
import StripePayment from './StripePayment';
import EWayPayment from './EWayPayment';
import SecurePayPayment from './SecurePayPayment';
import NabTransactPayment from './NabTransactPayment';

export interface OutstandingPaymentFormProps {
  orderHash: string
}

const OutstandingPaymentForm: FC<OutstandingPaymentFormProps> = ({ orderHash }) => {
  const [errors, setErrors] = useState<string[]>([]);
  const [paymentDetails, setPaymentDetails] = useState<PaymentIntent | EwayAccessCode | SecurePayAccessCode | null>(null);
  const [gateway, setGateway] = useState<'stripe' | 'eWay' | 'secure_pay' | 'nab_transact' | null>(null);

  const { createTenantApiRequest } = useTenantApi();
  const queryClient = useQueryClient();

  const { mutate: prepareOutstandingPaymentMutate, isLoading: isPaymentLoading } = useMutation<ApiResponse<PaymentIntent | EwayAccessCode | SecurePayAccessCode>, Error, PrepareOutstandingPaymentRequest>({
    mutationFn: async (prepareOutstandingPaymentData: PrepareOutstandingPaymentRequest) => {
      return await prepareOutstandingPayment(createTenantApiRequest)(prepareOutstandingPaymentData);
    },
    onSuccess: (result) => {
      if (result.errors && result.errors.length > 0) {
        setErrors(result.errors);
      } else {
        const paymentDetail = result.items[0];
        setPaymentDetails(paymentDetail);
        setGateway(paymentDetail.gateway as 'stripe' | 'eWay' | 'secure_pay' | 'nab_transact');
        void queryClient.invalidateQueries({ queryKey: [orderQueryKey, { orderHash }] });
      }
    },
    onError: (error: any) => {
      setErrors([error.message ?? 'Failed to prepare outstanding payment.']);
    }
  });

  useEffect(() => {
    if (orderHash && !isPaymentLoading && !paymentDetails) {
      setErrors([]);
      prepareOutstandingPaymentMutate({ orderHash });
    }
  }, [orderHash, prepareOutstandingPaymentMutate]);

  if (isPaymentLoading) {
    return <SkeletonText mt='4' noOfLines={4} spacing='4' skeletonHeight='8' />;
  }

  return (
    <>
      {gateway === 'stripe' && paymentDetails && (
        <StripePayment
          isValid={errors.length === 0}
          setErrors={setErrors}
          options={paymentDetails as PaymentIntent}
        />
      )}
      {gateway === 'eWay' && paymentDetails && (
        <EWayPayment
          isValid={errors.length === 0}
          setErrors={setErrors}
          options={paymentDetails as EwayAccessCode}
        />
      )}
      {gateway === 'secure_pay' && paymentDetails && (
        <SecurePayPayment
          isValid={errors.length === 0}
          setErrors={setErrors}
          options={paymentDetails as SecurePayAccessCode}
        />
      )}
      {
        gateway === 'nab_transact' && paymentDetails && (
          <NabTransactPayment
            isValid={errors.length === 0}
            setErrors={setErrors}
            options={paymentDetails as NabTransactOptions}
          />
        )
      }
      {errors.length > 0 && (
        <BaseMessageBarError>
          {errors.join('. ')}
        </BaseMessageBarError>
      )}
    </>
  );
};

export default OutstandingPaymentForm;
